<script setup>
import getUuid from 'widget/helpers/uuid';
import { ref, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import WaveSurfer from 'wavesurfer.js';
import RecordPlugin from 'wavesurfer.js/dist/plugins/record.js';
import { format, intervalToDuration } from 'date-fns';
import { useAlert } from 'dashboard/composables';
import { convertAudio, convertToWav } from './utils/audioConversionUtils';

const props = defineProps({
  audioRecordFormat: {
    type: String,
    required: true,
  },
});

const emit = defineEmits([
  'recorderProgressChanged',
  'finishRecord',
  'pause',
  'play',
]);

const { t } = useI18n();

const waveformContainer = ref(null);
const wavesurfer = ref(null);
const record = ref(null);
const isRecording = ref(false);
const isPlaying = ref(false);
const hasRecording = ref(false);
const recordedAudioUrl = ref(null);

const formatTimeProgress = time => {
  const duration = intervalToDuration({ start: 0, end: time });
  return format(
    new Date(0, 0, 0, 0, duration.minutes, duration.seconds),
    'mm:ss'
  );
};

const AUDIO_EXTENSION_MAP = {
  'audio/ogg': 'ogg',
  'audio/mp3': 'mp3',
  'audio/mpeg': 'mp3',
  'audio/wav': 'wav',
  'audio/webm': 'webm',
};

const getRecordPluginOptions = audioFormat => {
  const options = {
    scrollingWaveform: true,
    renderRecordedAudio: false,
  };
  if (
    audioFormat === 'audio/ogg' &&
    MediaRecorder.isTypeSupported('audio/ogg;codecs=opus')
  ) {
    options.mimeType = 'audio/ogg;codecs=opus';
  }
  return options;
};

const initWaveSurfer = () => {
  wavesurfer.value = WaveSurfer.create({
    container: waveformContainer.value,
    waveColor: '#1F93FF',
    progressColor: '#6E6F73',
    height: 100,
    barWidth: 2,
    barGap: 1,
    barRadius: 2,
    plugins: [
      RecordPlugin.create(getRecordPluginOptions(props.audioRecordFormat)),
    ],
  });

  wavesurfer.value.on('pause', () => emit('pause'));
  wavesurfer.value.on('play', () => emit('play'));

  record.value = wavesurfer.value.plugins[0];

  wavesurfer.value.on('finish', () => {
    isPlaying.value = false;
  });

  // La conversión puede fallar según el equipo del usuario (micrófonos
  // estéreo/multicanal, headsets Bluetooth, blobs que el navegador no sabe
  // decodificar). La grabación NUNCA debe perderse por eso: cadena de
  // fallbacks OGG/MP3 → WAV → audio original tal cual lo grabó el navegador.
  // Se usa el tipo real del blob convertido (Safari cae a MP3 en vez de OGG)
  // para que nombre, content-type y flag de nota de voz sean coherentes.
  const buildAudioFile = async blob => {
    try {
      const audioBlob = await convertAudio(blob, props.audioRecordFormat);
      const audioType = audioBlob.type || props.audioRecordFormat;
      const ext = AUDIO_EXTENSION_MAP[audioType] || 'mp3';
      return new File([audioBlob], `${getUuid()}.${ext}`, { type: audioType });
    } catch (convError) {
      // eslint-disable-next-line no-console
      console.log(
        '[AudioRecorder] conversión falló, fallback a WAV:',
        convError
      );
    }

    try {
      const wavBlob = await convertToWav(blob);
      return new File([wavBlob], `${getUuid()}.wav`, { type: 'audio/wav' });
    } catch (wavError) {
      // eslint-disable-next-line no-console
      console.log(
        '[AudioRecorder] WAV falló, se envía el audio original:',
        wavError
      );
    }

    const mimeType = blob.type.split(';')[0] || 'audio/webm';
    const extension = (mimeType.split('/')[1] || 'webm').replace('x-', '');
    return new File([blob], `${getUuid()}.${extension}`, { type: mimeType });
  };

  record.value.on('record-end', async blob => {
    isRecording.value = false;

    if (!blob || blob.size === 0) {
      // eslint-disable-next-line no-console
      console.log('[AudioRecorder] grabación vacía: no se adjunta nada');
      useAlert(t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ERROR'));
      return;
    }

    const file = await buildAudioFile(blob);
    // eslint-disable-next-line no-console
    console.log(
      `[AudioRecorder] adjunto listo: ${file.name} (${file.type}, ${file.size} bytes)`
    );
    if (recordedAudioUrl.value) URL.revokeObjectURL(recordedAudioUrl.value);
    recordedAudioUrl.value = URL.createObjectURL(file);
    wavesurfer.value.load(recordedAudioUrl.value);
    emit('finishRecord', {
      name: file.name,
      type: file.type,
      size: file.size,
      file,
    });
    hasRecording.value = true;
  });

  record.value.on('record-progress', time => {
    emit('recorderProgressChanged', formatTimeProgress(time));
  });
};

const stopRecording = () => {
  if (isRecording.value) {
    record.value.stopRecording();
    isRecording.value = false;
  }
};

const startRecording = () => {
  record.value.startRecording();
  isRecording.value = true;
};

const playPause = () => {
  if (hasRecording.value) {
    wavesurfer.value.playPause();
    isPlaying.value = !isPlaying.value;
  }
};

onMounted(() => {
  initWaveSurfer();
  startRecording();
});

onUnmounted(() => {
  if (recordedAudioUrl.value) {
    URL.revokeObjectURL(recordedAudioUrl.value);
    recordedAudioUrl.value = null;
  }
  if (wavesurfer.value) {
    wavesurfer.value.destroy();
  }
});

defineExpose({ playPause, stopRecording, record });
</script>

<template>
  <div ref="waveformContainer" class="w-full p-1" />
</template>
